// 3D-aware miter pair detection.
//
// Two flanges form a miter pair when their bend-tangent corners (local (0,0,0)
// or (0,W,0)) coincide in WORLD coordinates AND their flange normals (or local
// +X axes) are perpendicular. This generalises the sibling-based detection used
// previously: it correctly handles
//   (a) two siblings on perpendicular parent edges meeting at a parent corner
//   (b) two cap flanges on different walls meeting at the top corner of a box
//   (c) any deeper combination as long as their 3D corners coincide.
//
// All math is done in plain {x,y,z} so this module can be safely imported by
// validate.ts and unfold.ts without dragging the three.js bundle into the
// initial chunk.

import { Part, Flange, FlangeId, EdgeKey } from './types';
import { deg2rad } from './bendMath';

interface Vec3 { x: number; y: number; z: number }
interface Frame {
  origin: Vec3;
  xAxis: Vec3;
  yAxis: Vec3;
  zAxis: Vec3;
}

const v3 = (x: number, y: number, z: number): Vec3 => ({ x, y, z });
const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const dist = (a: Vec3, b: Vec3): number => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

function localToWorld(frame: Frame, lx: number, ly: number, lz: number): Vec3 {
  return {
    x: frame.origin.x + frame.xAxis.x * lx + frame.yAxis.x * ly + frame.zAxis.x * lz,
    y: frame.origin.y + frame.xAxis.y * lx + frame.yAxis.y * ly + frame.zAxis.y * lz,
    z: frame.origin.z + frame.xAxis.z * lx + frame.yAxis.z * ly + frame.zAxis.z * lz,
  };
}

function localToWorldDir(frame: Frame, dir: Vec3): Vec3 {
  return {
    x: frame.xAxis.x * dir.x + frame.yAxis.x * dir.y + frame.zAxis.x * dir.z,
    y: frame.xAxis.y * dir.x + frame.yAxis.y * dir.y + frame.zAxis.y * dir.z,
    z: frame.xAxis.z * dir.x + frame.yAxis.z * dir.y + frame.zAxis.z * dir.z,
  };
}

function getEdgeFrame(parent: Flange, edge: EdgeKey) {
  const top = v3(0, 0, 1);
  switch (edge) {
    case '+x': return { edgeStart: v3(parent.length, 0, 0), v: v3(0, 1, 0), u: v3(1, 0, 0), top };
    case '-x': return { edgeStart: v3(0, 0, 0), v: v3(0, 1, 0), u: v3(-1, 0, 0), top };
    case '+y': return { edgeStart: v3(0, parent.width, 0), v: v3(1, 0, 0), u: v3(0, 1, 0), top };
    case '-y': return { edgeStart: v3(0, 0, 0), v: v3(1, 0, 0), u: v3(0, -1, 0), top };
  }
}

// Walk the part tree and produce world-space frames for every flange. Mirrors
// the math in geometry.ts/computeTransforms but uses plain Vec3 to stay free of
// the three.js dependency.
//
// `logical=true` skips the bend-radius offset (RT terms): the origin sits
// directly on the parent edge corner instead of "lifted" by R+T/2. This is
// the right reference for detecting 3D corner meetings, since two flanges
// emanating from the same parent corner are physically separated by ~2·RT
// after the bend and won't compare equal otherwise.
export function computeFlangeFrames(part: Part, logical = false): Record<FlangeId, Frame> {
  const T = part.thickness;
  const out: Record<FlangeId, Frame> = {};

  const walk = (id: FlangeId, parentFrame: Frame | null) => {
    const f = part.flanges[id];
    if (!f) return;

    if (!f.attachment || !parentFrame) {
      out[id] = {
        origin: v3(0, 0, 0),
        xAxis: v3(1, 0, 0),
        yAxis: v3(0, 1, 0),
        zAxis: v3(0, 0, 1),
      };
    } else {
      const parent = part.flanges[f.attachment.parentId];
      const ef = getEdgeFrame(parent, f.attachment.edge);
      const theta = deg2rad(f.attachment.bendAngle);
      const s = Math.sin(theta), c = Math.cos(theta);
      const sgn = Math.sign(f.attachment.bendAngle) || 1;
      const RT = logical ? 0 : f.attachment.bendRadius + T / 2;
      const off = f.attachment.offset;
      const uShift = RT * Math.abs(s);
      const tShift = sgn * RT * (1 - c);

      const localOrigin: Vec3 = {
        x: ef.edgeStart.x + ef.v.x * off + ef.u.x * uShift + ef.top.x * tShift,
        y: ef.edgeStart.y + ef.v.y * off + ef.u.y * uShift + ef.top.y * tShift,
        z: ef.edgeStart.z + ef.v.z * off + ef.u.z * uShift + ef.top.z * tShift,
      };
      const xAxisLocal: Vec3 = {
        x: ef.u.x * c + ef.top.x * s,
        y: ef.u.y * c + ef.top.y * s,
        z: ef.u.z * c + ef.top.z * s,
      };
      const yAxisLocal: Vec3 = ef.v;
      const zAxisLocal: Vec3 = {
        x: -ef.u.x * s + ef.top.x * c,
        y: -ef.u.y * s + ef.top.y * c,
        z: -ef.u.z * s + ef.top.z * c,
      };

      out[id] = {
        origin: localToWorld(parentFrame, localOrigin.x, localOrigin.y, localOrigin.z),
        xAxis: localToWorldDir(parentFrame, xAxisLocal),
        yAxis: localToWorldDir(parentFrame, yAxisLocal),
        zAxis: localToWorldDir(parentFrame, zAxisLocal),
      };
    }

    for (const cid of f.children) walk(cid, out[id]);
  };

  walk(part.rootId, null);
  return out;
}

export interface MiterCandidate {
  a: { flangeId: FlangeId; end: 'low' | 'high' };
  b: { flangeId: FlangeId; end: 'low' | 'high' };
}

// All pairings whose bend-tangent corners coincide in 3D and whose local +X axes
// are perpendicular — regardless of whether either flange has miterCorner enabled.
// `validate` uses this to surface "no buddy" / "one-sided" warnings.
export function findMiterCandidates(part: Part): MiterCandidate[] {
  // Use logical frames (no bend-radius offset) so two flanges sharing a parent
  // corner compare equal even though their physical bend ends are pushed apart
  // by R+T/2 in each direction.
  const frames = computeFlangeFrames(part, true);
  const tol = 0.5;

  type Cand = { flangeId: FlangeId; end: 'low' | 'high'; pos: Vec3 };
  const cands: Cand[] = [];
  for (const id of Object.keys(part.flanges)) {
    const f = part.flanges[id];
    if (!f.attachment) continue; // root has no bend tangent
    const fr = frames[id];
    if (!fr) continue;
    cands.push({ flangeId: id, end: 'low', pos: localToWorld(fr, 0, 0, 0) });
    cands.push({ flangeId: id, end: 'high', pos: localToWorld(fr, 0, f.width, 0) });
  }

  const pairs: MiterCandidate[] = [];
  const used = new Set<string>();
  const key = (c: { flangeId: FlangeId; end: 'low' | 'high' }) => `${c.flangeId}:${c.end}`;

  for (let i = 0; i < cands.length; i++) {
    const a = cands[i];
    if (used.has(key(a))) continue;
    for (let j = i + 1; j < cands.length; j++) {
      const b = cands[j];
      if (a.flangeId === b.flangeId) continue;
      if (used.has(key(b))) continue;
      if (dist(a.pos, b.pos) > tol) continue;

      // Two flanges form a corner miter when their bend-tangent directions
      // (yAxis = along the parent edge they attach to) are perpendicular.
      // Using zAxis (top normal) misclassifies cap-cap pairs that both face
      // downward; using xAxis misclassifies sibling walls that both point up.
      const yA = frames[a.flangeId].yAxis;
      const yB = frames[b.flangeId].yAxis;
      if (Math.abs(dot(yA, yB)) > 0.1) continue; // not perpendicular

      pairs.push({ a: { flangeId: a.flangeId, end: a.end }, b: { flangeId: b.flangeId, end: b.end } });
      used.add(key(a));
      used.add(key(b));
      break;
    }
  }
  return pairs;
}

// Active pairs = both flanges have miterCorner enabled.
export function findActiveMiterPairs(part: Part): MiterCandidate[] {
  return findMiterCandidates(part).filter((p) => {
    const fa = part.flanges[p.a.flangeId];
    const fb = part.flanges[p.b.flangeId];
    return fa?.attachment?.miterCorner === true && fb?.attachment?.miterCorner === true;
  });
}

// Lookup: which buddy (and at what end) does `flangeId` meet at `end`? Returns
// null if there is no candidate (regardless of miter-on flag).
export function findCandidateAt(
  candidates: MiterCandidate[],
  flangeId: FlangeId,
  end: 'low' | 'high',
): { id: FlangeId; end: 'low' | 'high' } | null {
  for (const c of candidates) {
    if (c.a.flangeId === flangeId && c.a.end === end) return { id: c.b.flangeId, end: c.b.end };
    if (c.b.flangeId === flangeId && c.b.end === end) return { id: c.a.flangeId, end: c.a.end };
  }
  return null;
}

// Lookup: any candidate involving `flangeId` (used to detect "no buddy" warnings).
export function findAnyCandidate(
  candidates: MiterCandidate[],
  flangeId: FlangeId,
): { id: FlangeId; selfEnd: 'low' | 'high'; buddyEnd: 'low' | 'high' } | null {
  for (const c of candidates) {
    if (c.a.flangeId === flangeId) return { id: c.b.flangeId, selfEnd: c.a.end, buddyEnd: c.b.end };
    if (c.b.flangeId === flangeId) return { id: c.a.flangeId, selfEnd: c.b.end, buddyEnd: c.a.end };
  }
  return null;
}
